// src/pages/MisPedidos.js
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import { routes } from '../routes';

// Moneda CRC
const formatCRC = (n) =>
  `₡${Number(n || 0).toLocaleString('es-CR', { minimumFractionDigits: 0 })}`;

// Normaliza cart (jsonb / string / objeto)
const normalizeCart = (cart) => {
  try {
    if (!cart) return [];
    if (Array.isArray(cart)) return cart;
    if (typeof cart === 'string') return JSON.parse(cart);
    if (cart && Array.isArray(cart.items)) return cart.items;
  } catch (e) {
    console.warn('No se pudo parsear cart:', e);
  }
  return [];
};

// Fecha legible
const formatDate = (iso) => {
  try {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('es-CR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

// ID corto para UI (UUID -> primeros 8; numérico -> últimos 6)
const shortOrderId = (id) => {
  if (id == null) return '';
  const s = String(id);
  if (s.includes('-')) return s.slice(0, 8); // UUID
  if (/^\d+$/.test(s)) return s.slice(-6);   // numérico
  return s.slice(0, 8);
};

export default function MisPedidosPage({ user, onNavigate, onRepeatOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState({}); // id -> bool
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user) return;

    const fetchPage = async () => {
      setLoading(true);
      setError('');
      try {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        // Usamos tus campos reales: attended y factura_numero
        const { data, error, count } = await supabase
          .from('orders')
          .select(
            'id, created_at, total, payment_method, cart, attended, factura_numero, cliente_id',
            { count: 'exact' }
          )
          .eq('cliente_id', user.id)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (error) throw error;

        setOrders((prev) => (page === 0 ? data || [] : [...prev, ...(data || [])]));
        if (typeof count === 'number') {
          setHasMore(to + 1 < count);
        } else {
          setHasMore((data || []).length === pageSize);
        }
      } catch (e) {
        console.error('Supabase error:', e);
        setError(e?.message || 'No pudimos cargar tus pedidos.');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [user, page]); // deps estándar (sin reglas especiales)

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const stats = useMemo(() => {
    const total = orders.length;
    const completed = orders.filter((o) => o.attended).length;
    const pending = total - completed;
    const spent = orders.reduce((sum, o) => sum + Number(o.total || 0), 0);
    return { total, completed, pending, spent };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((o) => {
      const idMatch = String(o.id).toLowerCase().includes(term);
      const facturaMatch = (o.factura_numero || '').toLowerCase().includes(term);
      const paymentMatch = (o.payment_method || '').toLowerCase().includes(term);
      return idMatch || facturaMatch || paymentMatch;
    });
  }, [orders, searchTerm]);


  if (!user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
          <h1 className="text-2xl font-bold mb-3">Debes iniciar sesión</h1>
          <p className="text-gray-600 mb-6">
            Para ver tus pedidos, inicia sesión con tu cuenta.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => onNavigate && onNavigate(routes.login)}
              className="bg-brand text-white px-5 py-3 rounded-lg hover:bg-brand/90"
            >
              Ir a login
            </button>
            <button
              onClick={() => onNavigate && onNavigate(routes.registro)}
              className="bg-gray-200 text-gray-800 px-5 py-3 rounded-lg hover:bg-gray-300"
            >
              Registrarme
            </button>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="container mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">Mis pedidos</h1>
            <p className="text-sm text-gray-500 mt-1">
              ID cliente: {`CLI${String(user.id).padStart(4, '0')}`}
            </p>
          </div>
          <div className="w-full lg:w-80 flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por número de pedido, factura o pago"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2 focus:ring-2 focus:ring-brand focus:outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                title="Limpiar búsqueda"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Pedidos totales" value={stats.total} accent="bg-indigo-50 text-indigo-700" />
          <StatCard label="Completados" value={stats.completed} accent="bg-green-50 text-green-700" />
          <StatCard label="En proceso" value={stats.pending} accent="bg-amber-50 text-amber-700" />
          <StatCard label="Total invertido" value={formatCRC(stats.spent)} accent="bg-gray-50 text-gray-700" />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {loading && page === 0 ? (
        <div className="bg-white rounded-2xl shadow p-6 text-center">Cargando…</div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-2xl shadow p-8 text-center">
          <p className="text-gray-700">
            {orders.length === 0
              ? 'No encontramos pedidos asociados a tu cuenta.'
              : 'No encontramos pedidos que coincidan con tu búsqueda.'}
          </p>
          <div className="mt-4 flex gap-3 justify-center">
            <button
              onClick={() => onNavigate && onNavigate(routes.home)}
              className="bg-brand text-white px-5 py-3 rounded-lg hover:bg-brand/90"
            >
              Seguir comprando
            </button>
            {orders.length > 0 && (
              <button
                onClick={() => setSearchTerm('')}
                className="border px-5 py-3 rounded-lg hover:bg-gray-50"
              >
                Limpiar filtro
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((o) => {
            const items = normalizeCart(o.cart);
            const itemCount = items.reduce(
              (s, it) => s + Number(it.quantity || 0),
              0
            );

            return (
              <div key={o.id} className="bg-white rounded-2xl shadow border p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Pedido</p>
                    <p className="text-xl font-semibold text-gray-900">#{shortOrderId(o.id)}</p>
                    {o.created_at && (
                      <p className="text-sm text-gray-500">{formatDate(o.created_at)}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 md:justify-end">
                    <div className="text-sm text-gray-600">
                      <p className="text-gray-400">Artículos</p>
                      <p className="text-base font-semibold text-gray-900">{itemCount}</p>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p className="text-gray-400">Total</p>
                      <p className="text-base font-semibold text-gray-900">{formatCRC(o.total)}</p>
                    </div>
                    <StatusPill attended={o.attended} />
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-2">
                      Factura electrónica
                    </p>
                    {o.factura_numero ? (
                      <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 font-mono text-sm text-gray-800">
                        {o.factura_numero}
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm">
                        Pendiente
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-2">
                      Método de pago
                    </p>
                    <p className="text-sm text-gray-700">{o.payment_method || 'No disponible'}</p>
                  </div>
                </div>

                <StatusTimeline attended={o.attended} />

                {expanded[o.id] && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-2 pr-2">Producto</th>
                          <th className="py-2 pr-2">Cantidad</th>
                          <th className="py-2 pr-2">Precio</th>
                          <th className="py-2 pr-2">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 pr-2 flex items-center gap-3">
                              {it.image && (
                                <img
                                  src={it.image}
                                  alt={it.name}
                                  className="w-10 h-10 rounded object-cover"
                                />
                              )}
                              <span className="font-medium text-gray-800">{it.name}</span>
                            </td>
                            <td className="py-2 pr-2">{it.quantity}</td>
                            <td className="py-2 pr-2">{formatCRC(it.price)}</td>
                            <td className="py-2 pr-2">
                              {formatCRC((it.price || 0) * (it.quantity || 0))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => toggle(o.id)}
                    className="px-4 py-2 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:border-brand hover:text-brand"
                  >
                    {expanded[o.id] ? 'Ocultar detalle' : 'Ver detalle'}
                  </button>
                  <button
                    onClick={() =>
                      window.open(
                        `https://wa.me/50689422525?text=Hola%20El%20Ancla%2C%20necesito%20ayuda%20con%20mi%20pedido%20${shortOrderId(
                          o.id
                        )}`,
                        '_blank'
                      )
                    }
                    className="px-4 py-2 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:border-brand hover:text-brand"
                  >
                    Soporte
                  </button>
                  {typeof onRepeatOrder === 'function' && (
                    <button
                      onClick={() => onRepeatOrder(items, o)}
                      className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700"
                    >
                      Volver a comprar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {orders.length > 0 && hasMore && (
        <div className="text-center mt-6">
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-6 py-3 rounded-lg border hover:bg-gray-50"
          >
            Cargar más
          </button>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-gray-100 bg-white px-5 py-4 shadow-sm">
    <p className="text-xs uppercase tracking-[0.3em] text-gray-400">{label}</p>
    <p className={`text-2xl font-bold mt-2 ${accent}`}>{value}</p>
  </div>
);

const StatusPill = ({ attended }) => {
  if (attended) {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-100">
        Procesado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">
      En proceso
    </span>
  );
};

const StatusTimeline = ({ attended }) => {
  const steps = [
    { label: 'Pedido recibido', done: true },
    { label: 'Procesando', done: attended },
    { label: 'Finalizado', done: attended },
  ];

  return (
    <div className="mt-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
        {steps.map((step, index) => (
          <div key={step.label} className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                step.done
                  ? 'bg-brand text-white border-brand'
                  : 'bg-white text-gray-400 border-gray-200'
              }`}
            >
              {step.done ? '✓' : index + 1}
            </div>
            <span
              className={`text-sm font-medium ${
                step.done ? 'text-gray-900' : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
